import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import Tooltip from '../Tooltip';
import { REQUIRED_FIELD_ERROR } from '../../utils/constants';

import { RichTextEditor } from '@mantine/tiptap';
import styles from './TextEditor.module.scss';

type TextEditorProps = {
  title?: string;
  requirementsText?: string | string[];
  onChange: (value: string) => void;
  value: string;
  onBlur: (value: boolean) => void;
  errText?: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  limit: number;
};

const TextEditor = ({
  title,
  onChange,
  value,
  requirementsText,
  errText,
  setError,
  limit,
}: TextEditorProps) => {

  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });
  
  const isEmptyContent = useCallback((html: string) => {
    if (!html) return true;
    // временный div для извлечения текста
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent?.trim() || '';
    return text.length === 0;
  }, []);

  const normalizeContent = useCallback((html: string) => {
    return isEmptyContent(html) ? null : html;
  }, [isEmptyContent]);

  useEffect(() => {
    if (!editor) return;

    // если HTML отличается — обновляем
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  const handler = () => {
    const isEmpty = editor.isEmpty || editor.getText().trim().length === 0;
    const isFull = editor.getHTML().length > limit;
    setError(isEmpty ? REQUIRED_FIELD_ERROR : isFull ? 'Размер превышен' : '');
  };

  useEffect(() => {
    if (!editor) return;
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.error]: errText,
      })}
    >
      {title && (
        <div className={styles.titleWrapper}>
          <p className={styles.title}>{title}</p>
          {requirementsText && (
            <Tooltip requirementsText={requirementsText} error={!!errText} />
          )}
        </div>
      )}
      <div className={classNames(styles.tipWrap, { [styles.error]: errText })}>
        <RichTextEditor editor={editor}>
          <RichTextEditor.Toolbar className={styles.toolbar}>
            <RichTextEditor.Bold title="Жирный" />
            <RichTextEditor.Italic title="Курсив" />
            <RichTextEditor.Underline title="Подчёркнутый" />
            <RichTextEditor.Strikethrough title="Зачёркнутый" />
            <RichTextEditor.ClearFormatting title="Очистить форматирование" />

            <RichTextEditor.H1 title="Заголовок 1" />
            <RichTextEditor.H2 title="Заголовок 2" />
            <RichTextEditor.H3 title="Заголовок 3" />
            <RichTextEditor.H4 title="Заголовок 4" />

            <RichTextEditor.BulletList title="Маркированный список" />
            <RichTextEditor.OrderedList title="Нумерованный список" />

            <RichTextEditor.Undo title="Отменить" />
            <RichTextEditor.Redo title="Повторить" />
          </RichTextEditor.Toolbar>

          <RichTextEditor.Content className={styles.content} />
        </RichTextEditor>

        <div className={styles.footer}>
          <span>Символов:</span>
          <span>&nbsp;{editor.getHTML().length}</span>
          <span>&nbsp;/&nbsp;{limit}</span>
        </div>
      </div>

      {errText && <p className={styles.errorText}>{errText}</p>}
    </div>
  );
};

export default TextEditor;
